// api
import type { EnergyDataPoint } from '@/api/generated/energyAnalysis/endpoints/energyData';

// external
import { DateTime } from 'luxon';

interface DatedValue {
  timestamp: DateTime;
  value: number;
}

export interface OverviewTrendTotals {
  last4Weeks: number;
  previous4Weeks: number;
}

const toDatedValues = (points: EnergyDataPoint[]): DatedValue[] =>
  points.flatMap((point) => {
    if (point.value == null) return [];
    const timestamp = DateTime.fromISO(point.timestamp);
    return timestamp.isValid ? [{ timestamp, value: point.value }] : [];
  });

/** Sums points on or after `since`, or null when no point qualifies. */
export const sumEnergySince = (
  points: EnergyDataPoint[],
  since: DateTime,
): number | null => {
  const inRange = toDatedValues(points).filter(
    ({ timestamp }) => timestamp >= since,
  );

  if (!inRange.length) return null;

  return inRange.reduce((sum, { value }) => sum + value, 0);
};

/**
 * Splits the series into the 4 weeks ending at the latest data point and the
 * 4 weeks before that. Returns null unless both windows contain data.
 */
export const calculateOverviewTrendTotals = (
  points: EnergyDataPoint[],
): OverviewTrendTotals | null => {
  const datedValues = toDatedValues(points);
  if (!datedValues.length) return null;

  const end = datedValues.reduce(
    (latest, { timestamp }) => (timestamp > latest ? timestamp : latest),
    datedValues[0].timestamp,
  );
  const last4WeeksStart = end.minus({ weeks: 4 });
  const previous4WeeksStart = end.minus({ weeks: 8 });

  const last4Weeks = datedValues.filter(
    ({ timestamp }) => timestamp > last4WeeksStart,
  );
  const previous4Weeks = datedValues.filter(
    ({ timestamp }) =>
      timestamp > previous4WeeksStart && timestamp <= last4WeeksStart,
  );

  if (!last4Weeks.length || !previous4Weeks.length) return null;

  const sum = (values: DatedValue[]) =>
    values.reduce((total, { value }) => total + value, 0);

  return {
    last4Weeks: sum(last4Weeks),
    previous4Weeks: sum(previous4Weeks),
  };
};
